import { useMemo, useState, type ReactNode } from "react";
import type { DeliverableDto, Locale, OrderLinksDto, PartnerDto, SalespageDeliveryDto } from "../../shared/dto";
import { formatMediumDateTime } from "../date-format";
import { salespagePublished, salespageTakeawayReached } from "../rail-model";
import { workflowPillClass } from "../workflow-pill";
import { WorkingAvatar } from "../WorkingAvatar";
import { InlineLockIcon } from "./shared";
import { SalespageDeliveryMenu } from "./PublishSection";
import "./order-links.css";

// F82: the sales page step is THREE phases inside one rail step - approval
// (the existing review flow), order links (this block), publication (the
// take-away menu, unlocked by the integration). Pure phase derivations are
// exported for tests; the components only mirror them.
//
// F99: the tariffs are PRINTED, not asked for. They come from the approved
// sales page (server truth, `state.source`), their names are read-only, and
// the client only adds one URL each. Writing them by hand is the emergency
// for a page the server could not read, and it never shows up first.

// F121: confirming and sending are ONE act, so there is no phase between "all
// filled" and "sent". `ready` replaces the old confirm/integrate pair; the
// stored `confirmed` flag is still server truth, it just is not a step the
// client has to walk through.
export type OrderLinksPhase =
  | "locked"        // page not approved yet
  | "fill"          // fields still empty or incomplete
  | "ready"         // every tariff has a URL, the one action is live
  | "sent"          // job queued, not started
  | "working"       // the links are being worked into the page
  | "integrated";   // milestone reached

export function orderLinksPhase(
  state: OrderLinksDto | null | undefined,
  pageApproved: boolean
): OrderLinksPhase {
  if (!pageApproved || !state) return "locked";
  if (state.integratedAt) return "integrated";
  if (state.integration) return state.integration.status === "sent" ? "sent" : "working";
  const complete = state.tariffs.length > 0 && state.tariffs.every((tariff) => Boolean(tariff.url));
  return complete ? "ready" : "fill";
}

export type PublicationPhase = "locked" | "ready" | "done";

// F120: the same fact the rail reads to call the whole step done, so the boxed
// phase and the rail step can never disagree (`salespageTakeawayReached` in
// src/rail-model.ts: the integration, the historic stripe unlock, or the
// server's own lock on the delivery menu for a cached pre-F82 payload).
//
// 🔴 F137: the phase now HAS a finished state. It had only `locked` and `next`,
// so whatever the client did, the pill said "Na řadě" forever - the audit's
// first finding and Pavel's own suspicion. `done` is the publication FACT,
// recorded by the first of the two acts (`salespagePublished` in
// src/rail-model.ts), never the phase merely being open.
export function publicationPhase(
  state: OrderLinksDto | null | undefined,
  delivery: SalespageDeliveryDto | null | undefined
): PublicationPhase {
  if (!salespageTakeawayReached(state, delivery)) return "locked";
  return salespagePublished(delivery) ? "done" : "ready";
}

interface EditableLink {
  id: string;
  name: string;
  url: string;
}

function linksFromState(state: OrderLinksDto | null | undefined): EditableLink[] {
  return (state?.tariffs ?? []).map((tariff) => ({
    id: tariff.id,
    name: tariff.name,
    url: tariff.url ?? ""
  }));
}

export function OrderLinksBlock(props: {
  state: OrderLinksDto | null | undefined;
  pageApproved: boolean;
  isOwner: boolean;
  csrfToken?: string;
  profilePhotoUrl: string | null;
  t: (key: string) => string;
  onReloadData?: () => Promise<void>;
}) {
  const phase = orderLinksPhase(props.state, props.pageApproved);
  const custom = props.state?.source === "manual";
  const initial = useMemo(() => {
    const fromState = linksFromState(props.state);
    // Manual mode starts with one empty row so there is something to type in.
    return custom && fromState.length === 0 ? [{ id: "", name: "", url: "" }] : fromState;
  }, [props.state, custom]);
  const [rows, setRows] = useState<EditableLink[]>(initial);
  const [baseline, setBaseline] = useState(initial);
  const [busy, setBusy] = useState<"integrate" | "manual" | null>(null);
  const [error, setError] = useState<"save" | "integrate" | "manual" | null>(null);
  if (baseline !== initial) {
    // Server state moved (reload, another window): adopt it and drop drafts.
    setBaseline(initial);
    setRows(initial);
  }
  // Nothing readable on the page AND the client has not asked to write the
  // rows: the block explains itself instead of handing over an empty form.
  const missing = props.state?.source === "none";
  const filled = rows.length > 0 && rows.every((row) => row.url.trim() !== "" && (!custom || row.name.trim() !== ""));
  // The rows that still block the one action. A hand-written row with no name
  // yet is named by its position, so the sentence never points at nothing.
  const missingLinks = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.url.trim() === "" || (custom && row.name.trim() === ""))
    .map(({ row, index }) => row.name.trim() || `${props.t("sales.orderLinks.rowLabel")} ${index + 1}`);

  if (phase === "locked") {
    return (
      <p className="order-links-locked inline-lock-copy">
        <InlineLockIcon />
        {props.t("sales.orderLinks.lockedCopy")}
      </p>
    );
  }
  if (!props.isOwner) {
    return <p className="order-links-note">{props.t("sales.orderLinks.ownerOnly")}</p>;
  }

  async function post(path: string, body?: Record<string, unknown>): Promise<Response | null> {
    if (!props.csrfToken) return null;
    return fetch(path, {
      method: body === undefined ? "POST" : "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Launchpad-CSRF": props.csrfToken,
        "Idempotency-Key": crypto.randomUUID()
      },
      body: JSON.stringify(body ?? {})
    });
  }

  async function saveLinks(): Promise<boolean> {
    // In the predefined mode the browser sends URLs only: the names are the
    // server's, and it ignores anything else we could put here (F99).
    const payload = rows.map((row) => custom
      ? { id: row.id, name: row.name.trim(), url: row.url.trim() }
      : { id: row.id, url: row.url.trim() });
    const response = await post("/api/delivery/order-links", custom
      ? { links: payload, manual: true }
      : { links: payload });
    if (!response?.ok) {
      setError("save");
      return false;
    }
    return true;
  }

  // The one way into the emergency mode. It exists only while the server
  // knows no tariffs; the server refuses it (409 tariffs_known) otherwise.
  async function enableManual() {
    setBusy("manual");
    setError(null);
    try {
      const response = await post("/api/delivery/order-links", { links: [], manual: true });
      if (!response?.ok) {
        setError("manual");
        return;
      }
      await props.onReloadData?.();
    } catch {
      setError("manual");
    } finally {
      setBusy(null);
    }
  }

  // F121: ONE action. It saves whatever the client last typed and orders the
  // insertion; the server confirms the set in the same call, so there is no
  // second button and no state where the client has confirmed but not sent.
  async function integrate() {
    setBusy("integrate");
    setError(null);
    try {
      if (!(await saveLinks())) return;
      const response = await fetch("/api/delivery/order-links/integrate", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken ?? "",
          "Idempotency-Key": crypto.randomUUID()
        },
        body: "{}"
      });
      if (!response.ok) {
        setError("integrate");
        return;
      }
      await props.onReloadData?.();
    } catch {
      setError("integrate");
    } finally {
      setBusy(null);
    }
  }

  if (phase === "integrated") {
    return (
      <div className="order-links-done" role="status" data-order-links-phase="integrated">
        <p>{props.t("sales.orderLinks.integratedCopy")}</p>
        <ul className="order-links-summary">
          {(props.state?.tariffs ?? []).map((tariff) => (
            <li key={tariff.id}>
              <strong>{tariff.name}</strong>
              <a className="quiet-link break-text" href={tariff.url ?? undefined} target="_blank" rel="noreferrer">
                {(tariff.url ?? "").replace(/^https:\/\//, "")}
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (phase === "sent" || phase === "working") {
    return (
      <div className="order-links-working" role="status" data-order-links-phase={phase}>
        <WorkingAvatar size="card" profilePhotoUrl={props.profilePhotoUrl} />
        <div>
          <strong>{props.t(phase === "sent" ? "sales.orderLinks.sentTitle" : "sales.orderLinks.workingTitle")}</strong>
          <p>{props.t("sales.orderLinks.workingCopy")}</p>
        </div>
      </div>
    );
  }

  if (missing) {
    return (
      <div className="order-links-missing" data-order-links-phase="missing">
        <strong>{props.t("sales.orderLinks.missingTitle")}</strong>
        <p>{props.t("sales.orderLinks.missingCopy")}</p>
        <button
          className="secondary-button"
          type="button"
          disabled={busy !== null}
          onClick={() => void enableManual()}
          data-order-links-action="manual"
        >
          {busy === "manual" ? props.t("sales.orderLinks.manualEnabling") : props.t("sales.orderLinks.manualEnable")}
        </button>
        {error === "manual" && <p className="message error-message" role="alert">{props.t("sales.orderLinks.saveError")}</p>}
      </div>
    );
  }

  return (
    <div className="order-links-block" data-order-links-phase={phase} data-order-links-source={props.state?.source}>
      <p className="order-links-intro">
        {props.t(custom ? "sales.orderLinks.introCustom" : "sales.orderLinks.intro")}
      </p>
      {custom && <p className="order-links-note order-links-manual-note">{props.t("sales.orderLinks.manualNote")}</p>}
      <div className="order-links-fields">
        {rows.map((row, index) => (
          <div className="order-links-row" key={row.id || `row-${index}`}>
            {custom ? (
              <input
                className="order-links-name"
                type="text"
                value={row.name}
                placeholder={props.t("sales.orderLinks.namePlaceholder")}
                aria-label={props.t("sales.orderLinks.nameLabel")}
                onChange={(event) => {
                  const next = [...rows];
                  next[index] = { ...row, name: event.target.value };
                  setRows(next);
                  setError(null);
                }}
              />
            ) : (
              // The name is a FACT of the approved page, so it is printed,
              // not offered for editing (F99).
              <span className="order-links-name order-links-tariff" data-order-links-tariff={row.id}>
                {row.name}
              </span>
            )}
            <input
              className="order-links-url"
              type="url"
              inputMode="url"
              value={row.url}
              placeholder={props.t("sales.orderLinks.urlPlaceholder")}
              aria-label={`${props.t("sales.orderLinks.urlLabel")} ${row.name || index + 1}`}
              onChange={(event) => {
                const next = [...rows];
                next[index] = { ...row, url: event.target.value };
                setRows(next);
                setError(null);
              }}
            />
            {custom && (
              <button
                className="text-button danger order-links-remove"
                type="button"
                onClick={() => setRows(rows.filter((_, other) => other !== index))}
              >
                {props.t("sales.orderLinks.remove")}
              </button>
            )}
          </div>
        ))}
      </div>
      {custom && (
        <button
          className="text-button order-links-add"
          type="button"
          onClick={() => setRows([...rows, { id: "", name: "", url: "" }])}
        >
          {props.t("sales.orderLinks.add")}
        </button>
      )}
      <div className="order-links-actions">
        <button
          className="primary-button"
          type="button"
          disabled={!filled || busy !== null}
          onClick={() => void integrate()}
          data-order-links-action="integrate"
        >
          {busy === "integrate" ? props.t("sales.orderLinks.integrating") : props.t("sales.orderLinks.integrate")}
        </button>
      </div>
      {/* F44/F85: a disabled button NAMES what still blocks it, and it names
          the rows rather than repeating the intro two paragraphs above. */}
      {!filled && (
        <p className="order-links-note" role="status">
          {props.t("sales.orderLinks.fillHint").replace("{list}", missingLinks.join(", "))}
        </p>
      )}
      {error === "save" && <p className="message error-message" role="alert">{props.t("sales.orderLinks.saveError")}</p>}
      {error === "integrate" && <p className="message error-message" role="alert">{props.t("sales.orderLinks.integrateError")}</p>}
    </div>
  );
}

/**
 * F137: the finished publication, in one sentence that names the two things a
 * client can act on - WHEN it happened and WHICH act did it. Everything below
 * it stays live, so this is a receipt, never a gate.
 */
function PublicationDone(props: {
  published: NonNullable<SalespageDeliveryDto["published"]>;
  locale: Locale;
  t: (key: string) => string;
}) {
  return (
    <p className="order-links-note publication-done" role="status" data-publication-done={props.published.via}>
      {props.t(`sales.phases.publishedVia.${props.published.via}`)
        .replace("{when}", formatMediumDateTime(props.published.at, props.locale))}
    </p>
  );
}

// The three phases of the sales page step, each with its own visible state.
export function SalespagePhases(props: {
  deliverable: DeliverableDto | undefined;
  orderLinks: OrderLinksDto | null | undefined;
  delivery: SalespageDeliveryDto | null | undefined;
  isOwner: boolean;
  csrfToken?: string;
  partner: PartnerDto | null;
  profilePhotoUrl: string | null;
  locale: Locale;
  t: (key: string) => string;
  onReloadData?: () => Promise<void>;
  reviewContent: ReactNode;
}) {
  const pageApproved = props.deliverable?.status === "approved";
  const linksPhase = orderLinksPhase(props.orderLinks, pageApproved);
  const publication = publicationPhase(props.orderLinks, props.delivery);
  const phase1State = !props.deliverable ? "working" : pageApproved ? "done" : "next";
  const phase2State = linksPhase === "locked"
    ? "locked"
    : linksPhase === "integrated"
      ? "done"
      : linksPhase === "sent" || linksPhase === "working"
        ? "working"
        : "next";
  const phase3State = publication === "locked" ? "locked" : publication === "done" ? "done" : "next";
  const phases = [
    {
      key: "approve",
      state: phase1State,
      body: props.reviewContent
    },
    {
      key: "links",
      state: phase2State,
      body: (
        <OrderLinksBlock
          state={props.orderLinks}
          pageApproved={pageApproved}
          isOwner={props.isOwner}
          csrfToken={props.csrfToken}
          profilePhotoUrl={props.profilePhotoUrl}
          t={props.t}
          onReloadData={props.onReloadData}
        />
      )
    },
    {
      key: "publish",
      state: phase3State,
      body: publication === "locked" ? (
        <p className="order-links-locked inline-lock-copy" data-salespage-phase-lock="publish">
          <InlineLockIcon />
          {props.t("sales.phases.publishLocked")}
        </p>
      ) : (
        props.isOwner && props.deliverable && props.delivery ? (
          <>
            {/* 🔴 F137 hotovo != zamek. The finished phase says WHEN it
                finished and by WHICH act, and then hands the client the very
                same menu: Pavel asked for exactly this ("mela by dal byt
                pristupna... pokud bude chtit udelat druhou akci a vratit
                se"). Nothing below is disabled, hidden or greyed. */}
            {publication === "done" && props.delivery.published && (
              <PublicationDone published={props.delivery.published} locale={props.locale} t={props.t} />
            )}
            <SalespageDeliveryMenu
              deliverable={props.deliverable}
              state={props.delivery}
              csrfToken={props.csrfToken}
              partner={props.partner}
              t={props.t}
              onReloadData={props.onReloadData}
              showTitle={false}
            />
          </>
        ) : (
          <p className="order-links-note">{props.t("sales.orderLinks.ownerOnly")}</p>
        )
      )
    }
  ];
  return (
    <ol className="salespage-phases" aria-label={props.t("sales.phases.listLabel")}>
      {phases.map((item, index) => (
        <li className={`salespage-phase phase-state-${item.state}`} key={item.key} data-salespage-phase={item.key}>
          <header className="salespage-phase-head">
            <span className="salespage-phase-number step-number-token" aria-hidden="true">{index + 1}</span>
            <h2>{props.t(`sales.phases.${item.key}`)}</h2>
            <span className={`status-pill ${workflowPillClass(item.state)}`}>
              {props.t(`states.${item.state}`)}
            </span>
          </header>
          <div className="salespage-phase-body">{item.body}</div>
        </li>
      ))}
    </ol>
  );
}
